import { bopApi } from '@/lib/api/client';
import { isNotFoundError } from '@/lib/api/error';
import { toEpochMs } from '@/utils/date';

// ---------------------------------------------------------------------------
// User statuses
// (mirrors bop-api src/core/user/userStatus.js)
// ---------------------------------------------------------------------------

export const USER_STATUS = {
  ACTIVE: 'active',
  BLOCKED: 'blocked',
  PENDING: 'pending',
  OFFBOARDED: 'offboarded',
  DELETED: 'deleted',
} as const;

export type UserStatusValue = (typeof USER_STATUS)[keyof typeof USER_STATUS];

export const USER_STATUS_VALUES: readonly UserStatusValue[] = Object.values(USER_STATUS);

// Only active users are selected by default in the export dialog — other
// statuses can be opted in as needed (legacy comment carried over).
export const DEFAULT_DOWNLOAD_STATUSES: readonly UserStatusValue[] = [USER_STATUS.ACTIVE];

// ---------------------------------------------------------------------------
// Version notes
// ---------------------------------------------------------------------------

/**
 * Editorial notes for specific terms versions, keyed by effective date (the version string,
 * e.g. `2025-04-06`). Dev-maintained annotations surfaced in the BOP UI to give ops context
 * about what a particular terms version represents.
 */
export const TERMS_VERSION_NOTES: Record<string, string> = {
  '2025-06-04': 'OTM → OTE migration terms.',
};

export function getTermsVersionNote(version: string | null | undefined): string | null {
  if (!version) return null;
  return TERMS_VERSION_NOTES[version] ?? null;
}

/** Hard-coded version label for the legacy Pro terms flag */
export const PRO_TERMS_VERSION = 'PRO';

// Search validation
export const EMAIL_REGEX = /^[^\s@]+@(?:[^\s@.]+\.)+[^\s@.]+$/;
export const PID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-6][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const VERSION_REGEX = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

// Opaque DynamoDB LastEvaluatedKey shapes, passed back verbatim and never
// inspected. Deliberate fix: legacy mistyped the agreements lek as
// `string` — both are typed as opaque objects here.
export type ProTermsLek = Record<string, unknown>;

export interface AgreementsLek {
  p: string;
  s: string;
}

/** Raw legacy exchange auth record from POST /v1/auth/getTermsAndConditions */
export interface TermsProRaw {
  /** 'profile::acc:<pid>' */
  hash_key: string;
  session_nbf?: number;
  /** account created — epoch seconds */
  created?: number;
  early_adopter?: boolean;
  email: string;
  /** email (legacy auth key) */
  owner_id?: string;
  /** acceptance timestamp — epoch seconds */
  pro_terms_accepted?: number;
}

export interface TermsProPage {
  data: TermsProRaw[];
  lek?: ProTermsLek;
}

/** Raw record from POST /v1/users/terms-agreements (already reduced to latest-per-pid per page) */
export interface TermsAgreementRaw {
  pid: string;
  /** sort key minus 'terms-' prefix, i.e. 'YYYY-MM-DD' */
  version: string;
  /** acceptance timestamp (ISO string) */
  date: string;
  /** account created — epoch seconds (from auth profile) */
  created: number;
  early_adopter: boolean;
  email: string;
}

export interface TermsAgreementsPage {
  data: TermsAgreementRaw[];
  lek?: AgreementsLek;
}

/**
 * Display row shared by both tabs (legacy `Terms`) — except `created`
 * and `date` stay RAW (epoch seconds / ISO string) instead of pre-formatted
 * strings so column sorting is chronological, not lexical (deliberate fix).
 * Cells format at render via utils/format.formatDate, which keeps
 * the legacy seconds-heuristic and 'not set' fallback.
 */
export interface TermsRow {
  pid: string;
  email: string;
  early_adopter: boolean;
  /** 'YYYY-MM-DD' effective date, or 'PRO' for the legacy flag */
  version: string;
  /** account created — epoch seconds */
  created: number | null;
  /** acceptance — epoch seconds (Pro) or ISO string (agreements) */
  date: number | string | null;
}

// ---------------------------------------------------------------------------
// Pure reshaping (unit-testable)
// ---------------------------------------------------------------------------

const PRO_PID_PREFIX = 'profile::acc:';

/** Port of the legacy terms.post.ts mapping, minus the date pre-formatting (deliberate fix) */
export const mapProTerms = (raw: TermsProRaw[]): TermsRow[] =>
  raw.map((item) => ({
    pid: item.hash_key.replace(PRO_PID_PREFIX, ''),
    email: item.email,
    early_adopter: Boolean(item.early_adopter),
    version: PRO_TERMS_VERSION,
    created: item.created ?? null,
    date: item.pro_terms_accepted ?? null,
  }));

export const mapTermsAgreements = (raw: TermsAgreementRaw[]): TermsRow[] =>
  raw.map((item) => ({
    pid: item.pid,
    email: item.email,
    early_adopter: Boolean(item.early_adopter),
    version: item.version,
    created: item.created ?? null,
    date: item.date ?? null,
  }));

/**
 * Acceptance time as epoch ms — tolerates seconds epochs (Pro) and ISO strings
 * (agreements) via the shared utils/date seconds-vs-ms heuristic; null and
 * unparseable input coerce to 0 so dedupe comparisons stay total.
 */
export const acceptanceEpochMs = (date: TermsRow['date']): number => {
  if (date === null) return 0;
  const epochMs = toEpochMs(date);
  return Number.isNaN(epochMs) ? 0 : epochMs;
};

/**
 * Deliberate fix: the server dedupes "latest acceptance per pid" only
 * within one scan page, so a pid whose records span pages appears twice in the
 * accumulated client list (legacy showed the duplicates). Keep the latest
 * acceptance per pid as pages arrive.
 */
export const dedupeLatestByPid = (rows: TermsRow[]): TermsRow[] => {
  const latestByPid = new Map<string, TermsRow>();
  for (const row of rows) {
    const existing = latestByPid.get(row.pid);
    if (!existing || acceptanceEpochMs(row.date) >= acceptanceEpochMs(existing.date)) {
      latestByPid.set(row.pid, row);
    }
  }
  return [...latestByPid.values()];
};

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * One ~100-row DynamoDB scan page of the legacy Pro terms; the caller drives the
 * LEK loop client-side. The legacy `fetchAllRecords`/`limit: 5000`
 * server-side loop is dead code (the lambda ignores `limit`) and is deliberately
 * not ported — only the page-level loop exists.
 */
export const getTermsProPage = (lek?: ProTermsLek) => bopApi.post('v1/auth/getTermsAndConditions', { json: { ...(lek ? { lek } : {}) } }).json<TermsProPage>();

/**
 * 404 = no matching user → empty result. Legacy folded EVERY non-200 into an
 * empty list; only the "not found" case is swallowed here so real
 * failures still surface to the operator (deliberate deviation).
 */
export const searchTermsPro = async (userSearchTerm: string): Promise<TermsProRaw[]> => {
  try {
    return await bopApi.post('v1/auth/searchTermsAndConditions', { json: { userSearchTerm } }).json<TermsProRaw[]>();
  } catch (error) {
    if (isNotFoundError(error)) return [];
    throw error;
  }
};

export const getTermsAgreementsPage = ({ lek, version }: { lek?: AgreementsLek; version?: string }) =>
  bopApi.post('v1/users/terms-agreements', { json: { ...(lek ? { lek } : {}), ...(version ? { version } : {}) } }).json<TermsAgreementsPage>();

/** 404 = the user has no agreement — a successful empty search result */
export const searchTermsAgreement = async ({ identifier, version }: { identifier: string; version?: string }): Promise<TermsAgreementRaw | null> => {
  try {
    return await bopApi.post('v1/user/terms-agreement', { json: { identifier, ...(version ? { version } : {}) } }).json<TermsAgreementRaw>();
  } catch (error) {
    if (isNotFoundError(error)) return null;
    throw error;
  }
};

/** Distinct effective dates, sorted newest first by the server */
export const getTermsVersions = () => bopApi.get('v1/terms/versions').json<{ versions: string[] }>();
